import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tree, Empty, Button, Space, Typography, Tooltip } from 'antd';
import {
  FolderOutlined,
  FileTextOutlined,
  PlusOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  BranchesOutlined,
  FontSizeOutlined,
  NumberOutlined,
  MailOutlined,
  PhoneOutlined,
  LinkOutlined,
  CaretDownOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  UploadOutlined,
  StarOutlined,
  DollarOutlined,
  PercentageOutlined,
  TableOutlined,
} from '@ant-design/icons';
import { useDroppable } from '@dnd-kit/core';
import { selectTemplateData, updateTemplateData } from '../../../../store/slices/rfpTemplateSlice';
import {
  selectElement,
} from '../../../../store/slices/rfpBuilderSlice';
import { produce } from 'immer';

const { Text } = Typography;

// Inject custom CSS for Tree component
const injectTreeStyles = () => {
  const styleId = 'custom-tree-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.innerHTML = `
    .ant-tree .ant-tree-node-selected {
      background-color: #F3E8FF !important;
    }
    .ant-tree .ant-tree-node-selected .ant-tree-title {
      color: #8B5CF6 !important;
      font-weight: 500;
    }
    .ant-tree .ant-tree-node-selected .ant-tree-iconEle svg {
      color: #8B5CF6 !important;
    }
    .ant-tree .ant-tree-treenode {
      padding: 2px 0 !important;
    }
    .ant-tree .ant-tree-node-content-wrapper {
      line-height: 20px !important;
      height: auto !important;
      min-height: 24px !important;
    }
    .ant-tree .ant-tree-title {
      display: inline-flex !important;
      align-items: center !important;
      width: 100% !important;
    }
  `;
  document.head.appendChild(style);
};


// Helper to parse path into indices
const parsePath = (path) => {
  const match = path.match(/sheet-(\d+)(?:-section-(\d+))?(?:-category-(\d+))?(?:-question-(\d+))?/);
  if (!match) return null;

  return {
    sheetIdx: parseInt(match[1]),
    sectionIdx: match[2] !== undefined ? parseInt(match[2]) : null,
    categoryIdx: match[3] !== undefined ? parseInt(match[3]) : null,
    questionIdx: match[4] !== undefined ? parseInt(match[4]) : null,
  };
};

// Get icon for field type
const getFieldTypeIcon = (fieldType) => {
  const iconMap = {
    text: <FontSizeOutlined />,
    textarea: <FileTextOutlined />,
    number: <NumberOutlined />,
    email: <MailOutlined />,
    phone: <PhoneOutlined />,
    url: <LinkOutlined />,
    select: <CaretDownOutlined />,
    multiselect: <CaretDownOutlined />,
    radio: <CheckSquareOutlined />,
    checkbox: <CheckSquareOutlined />,
    date: <CalendarOutlined />,
    datetime: <CalendarOutlined />,
    time: <ClockCircleOutlined />,
    daterange: <CalendarOutlined />,
    file: <UploadOutlined />,
    rating: <StarOutlined />,
    currency: <DollarOutlined />,
    percentage: <PercentageOutlined />,
    table: <TableOutlined />,
  };
  return iconMap[fieldType] || <FileTextOutlined />;
};

export default function BuilderCanvas() {
  const dispatch = useDispatch();
  const templateData = useSelector(selectTemplateData);
  const [hoveredNode, setHoveredNode] = useState(null);

  // Make entire canvas droppable (fallback to first category of first section)
  const { setNodeRef: setCanvasRef, isOver: isCanvasOver } = useDroppable({
    id: 'builder-canvas-root',
    data: {
      sheetIdx: 0,
      sectionIdx: 0,
      categoryIdx: 0,
    },
  });

  // Inject custom tree styles on mount
  useEffect(() => {
    injectTreeStyles();
  }, []);

  // Move item up/down within same container
  const handleMove = (path, direction) => {
    const indices = parsePath(path);
    if (!indices) return;

    const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = indices;

    const newData = produce(templateData, (draft) => {
      let array, index;

      if (questionIdx !== null) {
        // Move question
        array = draft.sheets[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions;
        index = questionIdx;
      } else if (categoryIdx !== null) {
        // Move category
        array = draft.sheets[sheetIdx].sections[sectionIdx].categories;
        index = categoryIdx;
      } else if (sectionIdx !== null) {
        // Move section
        array = draft.sheets[sheetIdx].sections;
        index = sectionIdx;
      } else {
        // Move sheet
        array = draft.sheets;
        index = sheetIdx;
      }

      if (!array || index < 0 || index >= array.length) return;

      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= array.length) return;

      // Swap items
      [array[index], array[newIndex]] = [array[newIndex], array[index]];
    });

    dispatch(updateTemplateData(newData));
  };

  // Droppable TreeNode wrapper
  const DroppableTreeNode = ({ nodeData, children }) => {
    const { setNodeRef, isOver } = useDroppable({
      id: nodeData.key,
      data: nodeData,  // Pass nodeData directly - dnd-kit wraps it in 'current' automatically
    });

    // Debug drop target
    if (isOver) {
      console.log('Hovering over:', nodeData.title, {
        sheetIdx: nodeData.sheetIdx,
        sectionIdx: nodeData.sectionIdx,
        categoryIdx: nodeData.categoryIdx,
      });
    }

    return (
      <div
        ref={setNodeRef}
        style={{
          background: isOver ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
          border: isOver ? '2px dashed #8B5CF6' : '2px dashed transparent',
          borderRadius: '4px',
          transition: 'all 0.15s',
          width: '100%',
          minHeight: '24px',
          display: 'flex',
          alignItems: 'center',
          padding: isOver ? '2px' : '0',
          boxShadow: isOver ? '0 0 8px rgba(139, 92, 246, 0.3)' : 'none',
          // Expand hit area to cover more of the tree node
          margin: '0 -8px',
          paddingLeft: '8px',
          paddingRight: '8px',
        }}
      >
        {children}
      </div>
    );
  };

  const buildTreeData = (data) => {
    if (!data || !data.sheets) {
      return [];
    }

    return data.sheets.map((sheet, sheetIdx) => ({
      key: `sheet-${sheetIdx}`,
      title: sheet.name || `Sheet ${sheetIdx + 1}`,
      icon: <FolderOutlined />,
      droppable: true,
      sheetIdx,
      isFirst: sheetIdx === 0,
      isLast: sheetIdx === data.sheets.length - 1,
      totalCount: data.sheets.length,
      children: sheet.sections?.map((section, sectionIdx) => ({
        key: `sheet-${sheetIdx}-section-${sectionIdx}`,
        title: section.name || `Section ${sectionIdx + 1}`,
        icon: <FolderOutlined />,
        droppable: true,
        sheetIdx,
        sectionIdx,
        isFirst: sectionIdx === 0,
        isLast: sectionIdx === sheet.sections.length - 1,
        totalCount: sheet.sections.length,
        children: section.categories?.map((category, catIdx) => ({
          key: `sheet-${sheetIdx}-section-${sectionIdx}-category-${catIdx}`,
          title: category.name || `Category ${catIdx + 1}`,
          icon: <FolderOutlined />,
          droppable: true,
          sheetIdx,
          sectionIdx,
          categoryIdx: catIdx,
          isFirst: catIdx === 0,
          isLast: catIdx === section.categories.length - 1,
          totalCount: section.categories.length,
          children: category.questions?.map((question, qIdx) => ({
            key: `sheet-${sheetIdx}-section-${sectionIdx}-category-${catIdx}-question-${qIdx}`,
            title: question.question || question.label || `Question ${qIdx + 1}`,
            icon: getFieldTypeIcon(question.field_type),
            isLeaf: true,
            isFirst: qIdx === 0,
            isLast: qIdx === category.questions.length - 1,
            totalCount: category.questions.length,
            hasConditionalLogic: !!question.conditionalLogic,
            fieldType: question.field_type,
          })),
        })),
      })),
    }));
  };

  const handleSelect = (selectedKeys, { node }) => {
    if (selectedKeys.length > 0) {
      dispatch(
        selectElement({
          type: node.isLeaf ? 'question' : 'container',
          path: selectedKeys[0],
          data: node,
        })
      );
    }
  };

  const handleExpand = (expandedKeys) => {
    // Toggle logic handled by Redux
    // This is just for display
  };

  const handleAddSheet = () => {
    const newData = produce(templateData, (draft) => {
      if (!draft.sheets) {
        draft.sheets = [];
      }
      draft.sheets.push({
        id: `sheet_${Date.now()}`,
        name: `Sheet ${draft.sheets.length + 1}`,
        sections: [],
      });
    });
    dispatch(updateTemplateData(newData));
  };

  const handleAddField = (fieldType, label) => {
    // Add field to first sheet by default
    const newData = produce(templateData, (draft) => {
      if (!draft.sheets || draft.sheets.length === 0) {
        draft.sheets = [{
          id: `sheet_${Date.now()}`,
          name: 'Main',
          sections: [],
        }];
      }

      const sheet = draft.sheets[0];
      if (!sheet.sections || sheet.sections.length === 0) {
        sheet.sections = [{
          id: `section_${Date.now()}`,
          name: 'General',
          categories: [],
        }];
      }

      const section = sheet.sections[0];
      if (!section.categories || section.categories.length === 0) {
        section.categories = [{
          id: `category_${Date.now()}`,
          name: 'Questions',
          questions: [],
        }];
      }

      const category = section.categories[0];
      if (!category.questions) {
        category.questions = [];
      }

      category.questions.push({
        id: `question_${Date.now()}`,
        question: `New ${label}`,
        field_type: fieldType,
        required: false,
        help_text: '',
        options: fieldType === 'select' || fieldType === 'multiselect' ? [] : undefined,
      });
    });

    dispatch(updateTemplateData(newData));
  };

  const treeData = buildTreeData(templateData);

  if (!templateData || treeData.length === 0) {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
          borderRadius: '12px',
          border: '2px dashed #E5E7EB',
          padding: '40px',
        }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Space direction="vertical" size="small">
              <Text type="secondary">Your template is empty</Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Add a sheet to get started
              </Text>
            </Space>
          }
        >
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddSheet}
            size="large"
            style={{ background: '#8B5CF6', borderColor: '#8B5CF6' }}
          >
            Add First Sheet
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div
      ref={setCanvasRef}
      style={{
        height: '100%',
        background: isCanvasOver ? 'rgba(139, 92, 246, 0.02)' : '#fff',
        borderRadius: '12px',
        border: '1px solid #E5E7EB',
        padding: '24px',
        overflow: 'auto',
        transition: 'background 0.15s',
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }}>
          <Text strong style={{ fontSize: '16px' }}>
            Template Structure
          </Text>
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAddSheet}
            size="small"
          >
            Add Sheet
          </Button>
        </Space>

        <Tree
          treeData={treeData}
          onSelect={handleSelect}
          showIcon={false}
          defaultExpandAll
          blockNode
          titleRender={(nodeData) => {
            const isHovered = hoveredNode === nodeData.key;
            const content = (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  paddingRight: '4px',
                  fontSize: '13px',
                  minHeight: '24px',
                }}
                onMouseEnter={() => setHoveredNode(nodeData.key)}
                onMouseLeave={() => setHoveredNode(null)}
              >
                {/* Icon inline with text */}
                <span style={{ fontSize: '14px', lineHeight: 1, display: 'flex', alignItems: 'center' }}>
                  {nodeData.icon}
                </span>

                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {nodeData.title}
                </span>

                {nodeData.hasConditionalLogic && (
                  <Tooltip title="Has conditional logic">
                    <BranchesOutlined style={{ fontSize: '11px', color: '#8B5CF6' }} />
                  </Tooltip>
                )}

                {(isHovered || nodeData.totalCount <= 3) && nodeData.totalCount > 1 && (
                  <Space size={2}>
                    <Tooltip title="Move up">
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={nodeData.isFirst}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMove(nodeData.key, 'up');
                        }}
                        style={{
                          padding: '0 2px',
                          height: '18px',
                          width: '18px',
                          fontSize: '10px',
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Move down">
                      <Button
                        type="text"
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={nodeData.isLast}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMove(nodeData.key, 'down');
                        }}
                        style={{
                          padding: '0 2px',
                          height: '18px',
                          width: '18px',
                          fontSize: '10px',
                        }}
                      />
                    </Tooltip>
                  </Space>
                )}
              </div>
            );

            // Wrap droppable nodes with larger drop zone
            if (nodeData.droppable) {
              return <DroppableTreeNode nodeData={nodeData}>{content}</DroppableTreeNode>;
            }

            return content;
          }}
          style={{
            fontSize: '13px',
          }}
        />
      </Space>
    </div>
  );
}
